import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sparkles, Scissors } from 'lucide-react';

// Styled Components
const HeroWrapper = styled.section`
  position: relative;
  height: 100vh;
  background-image: url('https://ik.imagekit.io/s0jjvjav7h/openart-image_wZM9rT4e_1768581571224_raw.png');
  overflow: hidden;
  background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    background-attachment: fixed;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: black;
  opacity: 0.2;
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.3;
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=');
`;

const HeroContent = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 1rem;
  z-index: 10;
`;

const HeroInner = styled.div`
  max-width: 896px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  animation: fadeInUp 0.8s ease-out;

  @media (min-width: 640px) {
    font-size: 3.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const GradientText = styled.span`
  background: linear-gradient(to right, #fef3c7, #fdda0d);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: white;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;

  @media (min-width: 640px) {
    font-size: 1.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const PrimaryButton = styled.button`
  background: white;
  color: #9333ea;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-weight: bold;
  font-size: 1.125rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #faf5ff;
    transform: scale(1.05);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 2px solid white;
  color: white;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-weight: bold;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: #9333ea;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const FloatingIcon = styled.div`
  position: absolute;
  animation: bounce 2s infinite;
  z-index: 5;

  @keyframes bounce {
    0%, 100% {
      transform: translateY(-25%);
      animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
    }
    50% {
      transform: translateY(0);
      animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }
  }
`;

const FloatingIconLeft = styled(FloatingIcon)`
  top: 80px;
  left: 40px;

  svg {
    width: 48px;
    height: 48px;
    color: #fef08a;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const FloatingIconRight = styled(FloatingIcon)`
  bottom: 80px;
  right: 40px;
  animation-delay: 0.5s;

  svg {
    width: 48px;
    height: 48px;
    color: #fbcfe8;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Component
const HeroSection = () => {
  const [banner, setBanner] = useState({
    src: 'https://ik.imagekit.io/s0jjvjav7h/openart-image_wZM9rT4e_1768581571224_raw.png', // Mặc định
    title: 'Vẻ Đẹp Hoàn Hảo',
    subtitle: 'Trải nghiệm dịch vụ chăm sóc tóc và móng đẳng cấp tại KM Nail Hair Salon'
  });

  useEffect(() => {
    // Gọi API lấy banners
    fetch("https://nk-salon-hair.germibays.workers.dev/api/banners")
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          // Lấy banner đầu tiên trong danh sách quản lý
          setBanner(data[0]);
        }
      })
      .catch(err => console.error("Lỗi banner:", err));
  }, []);

  const handleBooking = () => document.getElementById('liên hệ')?.scrollIntoView({ behavior: 'smooth' });
  const handlePricing = () => document.getElementById('dịch vụ')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <HeroWrapper style={{ backgroundImage: `url(${banner.src})` }}>
      <HeroOverlay />
      <HeroPattern />

      <HeroContent>
        <HeroInner>
          <HeroTitle>
            {banner.title.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}<br />
              </React.Fragment>
            ))}
            {/* Nếu title của bạn có phần muốn bôi màu, bạn có thể quy ước gán Title trong Admin */}
            {/* Ở đây mặc định dòng 2 sẽ là GradientText nếu title Admin để trống dòng 2 */}
            {!banner.title.includes('\n') && <GradientText>Từ Đầu Đến Chân</GradientText>}
          </HeroTitle>

          <HeroSubtitle>
            {banner.subtitle}
          </HeroSubtitle>

          <ButtonGroup>
            <PrimaryButton onClick={handleBooking}>Đặt Lịch Ngay</PrimaryButton>
            <SecondaryButton onClick={handlePricing}>Xem Bảng Giá</SecondaryButton>
          </ButtonGroup>
        </HeroInner>
      </HeroContent>

      <FloatingIconLeft><Sparkles /></FloatingIconLeft>
      <FloatingIconRight><Scissors /></FloatingIconRight>
    </HeroWrapper>
  );
};

export default HeroSection;